// frontend/src/components/Category.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../services/api'; // Axios instance with token interceptor

const Category = () => {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Get user role from localStorage
  useEffect(() => {
    const role = localStorage.getItem('role');
    setUserRole(role);
  }, []);

  // Fetch categories when the component mounts.
  useEffect(() => {
    fetchCategories();
  }, []);

  // Get all categories from the API.
  const fetchCategories = async () => {
    try {
      console.log('Fetching categories from:', api.defaults.baseURL);
      const response = await api.get('/category');
      console.log('Categories response:', response.data);
      setCategories(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching categories:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      if (error.response?.status === 401) {
        // Redirect to login on unauthorized access.
        navigate('/login');
      } else if (error.code === 'ERR_NETWORK') {
        setError('Cannot connect to server. Please check if the backend is running.');
      } else {
        setError(`Failed to fetch categories: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  // Open the dialog for adding a new category or editing an existing one.
  const handleOpenDialog = (category = null) => {
    if (category) {
      setNewCategory(category.name);
      setIsEditing(true);
      setEditingCategoryId(category._id);
    } else {
      setNewCategory('');
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  // Submit the form to create or update a category.
  const handleSubmitCategory = async () => {
    try {
      if (isEditing) {
        await api.put(`/category/${editingCategoryId}`, { name: newCategory });
      } else {
        await api.post('/category', { name: newCategory });
      }
      setOpenDialog(false);
      fetchCategories(); // Refresh the list after changes.
      setError('');
    } catch (error) {
      console.error('Error submitting category:', error);
      if (error.response?.status === 403) {
        setError('Access denied: You do not have permission to perform this action.');
      } else {
        setError('Failed to save category. Please try again.');
      }
    }
  };

  // Delete a category after user confirmation.
  const handleDeleteCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await api.delete(`/category/${id}`);
        fetchCategories();
        setError('');
      } catch (error) {
        console.error('Error deleting category:', error);
        if (error.response?.status === 403) {
          setError('Access denied: You do not have permission to delete categories.');
        } else {
          setError('Failed to delete category. Please try again.');
        }
      }
    }
  };

  const isAdmin = userRole === 'admin';

  return (
    <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
              <Typography variant="h5" gutterBottom align="center">
        {isAdmin ? 'Manage Categories' : 'View Categories'}
      </Typography>
      
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Add New Category Button - Admin Only */}
      {isAdmin && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenDialog()}
            startIcon={<AddIcon />}
          >
            Add New Category
          </Button>
        </Box>
      )}

      {/* Categories Grid */}
      <Grid container spacing={2}>
        {categories.map((cat) => (
          <Grid item xs={12} sm={6} md={4} key={cat._id}>
            <Box
              sx={{
                border: '1px solid #ccc',
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': { boxShadow: 3, transform: 'scale(1.02)' },
              }}
            >
              <Typography variant="h6" sx={{ mb: 1 }}>
                {cat.name}
              </Typography>
              
              {/* Action Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {isAdmin ? (
                  // Admin actions: Edit and Delete
                  <>
                    <Tooltip title="Edit Category">
                      <IconButton onClick={() => handleOpenDialog(cat)} color="primary">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Category">
                      <IconButton onClick={() => handleDeleteCategory(cat._id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  // Regular user: View only
                  <Tooltip title="View Category">
                    <IconButton color="info" disabled>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Grid>
        ))}
        {categories.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body1" align="center">
              {isAdmin ? 'No categories available. Create your first category!' : 'No categories available.'}
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Category Dialog - Admin Only */}
      {isAdmin && (
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
          <DialogTitle>{isEditing ? 'Edit Category' : 'Add New Category'}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Category Name"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              autoFocus
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleSubmitCategory} color="primary">
              {isEditing ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default Category;
